import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useMsal } from '@azure/msal-react';
import { acquireApiToken } from './tokenAcquisition';
import { api, HttpError } from '../api/client';
import type { MeProfile } from '../api/types';

/**
 * The four terminal/transient states the gate switches on:
 *  - `loading`    — resolving (token acquire + /api/me in flight). Show a spinner.
 *  - `authorized` — /api/me returned a profile. Render the app.
 *  - `noAccess`   — a VALID token was sent but the API has no DorUsers row for this user
 *                   (200 + null body). Genuinely not provisioned → no-access screen.
 *  - `error`      — token acquisition failed, OR /api/me failed with a transient/infra
 *                   error (502/5xx/network/401/403). NEVER shown as no-access — the user
 *                   can retry / switch account. This is the SPA-Auth Standard rule 5 fix:
 *                   an infrastructure failure must not read as "you're denied".
 */
export type AccessStatus = 'loading' | 'authorized' | 'noAccess' | 'error';

export interface AccessDiagnostics {
  /** The signed-in account (rule 7 — show what the app saw). */
  email: string | null;
  /** MSAL token-acquisition error code/message, if any. */
  tokenError: string | null;
  /** /api/me transport error detail, if any. */
  profileError: string | null;
  /** /api/me HTTP status, if it errored. */
  profileStatus: number | null;
}

export interface AccessContextValue {
  status: AccessStatus;
  /** The resolved DorUsers profile (null until authorized / when not provisioned). */
  profile: MeProfile | null;
  diagnostics: AccessDiagnostics;
  /** Re-run the token + /api/me resolution (used by the error screen's Retry). */
  retry: () => void;
}

const AccessContext = createContext<AccessContextValue | null>(null);

/**
 * Resolves the signed-in user's authorisation for the gate. Mirrors the template's
 * `TenantProvider` but, because DOR authorises on the API's view (a DorUsers row via
 * /api/me) rather than token-role claims, it:
 *
 *  1. acquires the API access token with `forceRefresh: true` (rules 2 + 3 + 4 — init
 *     first, fresh token, guarded interactive fallback incl. `timed_out`), THEN
 *  2. resolves /api/me with that token (rule 1 — authorise on the API view, only AFTER
 *     a successful token acquire).
 *
 * Crucially it separates "couldn't get a token / couldn't reach the API" (`error`) from
 * "valid token, but you're not provisioned" (`noAccess`) — so a 502 or an expired SSO
 * session never dead-ends the user on the no-access screen (rule 5).
 *
 * Mounted by `RootGate` ONLY once MSAL reports an authenticated account.
 */
export function AccessProvider({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  // Derive the account from msal-react's memoised `accounts` (a STABLE reference) — NOT
  // `instance.getActiveAccount()`, which returns a FRESH object on every call and would make
  // the resolution effect below re-run on every render (a "Loading your workspace…" death
  // loop). Key the effect on the stable `homeAccountId` so it runs once per signed-in identity.
  const account = accounts[0] ?? null;
  const accountId = account?.homeAccountId ?? null;

  const [status, setStatus] = useState<AccessStatus>('loading');
  const [profile, setProfile] = useState<MeProfile | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileStatus, setProfileStatus] = useState<number | null>(null);
  const [nonce, setNonce] = useState(0); // bumping this re-runs the resolution (Retry)

  useEffect(() => {
    let cancelled = false;
    if (!account) {
      // RootGate only mounts us when authenticated; this is a defensive guard.
      setStatus('loading');
      return;
    }
    setStatus('loading');
    setProfile(null);
    setTokenError(null);
    setProfileError(null);
    setProfileStatus(null);

    void (async () => {
      // (2) initialize + (3) forceRefresh + (4) guarded interactive fallback (incl. timed_out)
      const token = await acquireApiToken(instance, account, { forceRefresh: true });
      if (cancelled) return;
      if (token === null) {
        // Either an interactive redirect is now in progress (we're navigating away), or
        // silent acquisition genuinely failed after the once-per-session interactive attempt.
        // Surface as a recoverable error — NOT no-access (rule 5).
        setTokenError('Could not acquire an access token (interactive sign-in may be required).');
        setStatus('error');
        return;
      }

      // (1) Authorise on the API's view, resolved only AFTER a successful token acquire.
      try {
        const me = await api.get<MeProfile | null>('/me');
        if (cancelled) return;
        if (me) {
          setProfile(me);
          setStatus('authorized');
        } else {
          // 200 + null body: the token was accepted but there's no DorUsers row for this
          // identity → genuinely not provisioned.
          setStatus('noAccess');
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof HttpError) {
          setProfileStatus(err.status);
          setProfileError(err.payload?.detail ?? `HTTP ${err.status}`);
        } else {
          setProfileError(err instanceof Error ? err.message : 'profile request failed');
        }
        // Any transport/infra error (502/5xx/network — and even 401/403 from this anonymous
        // endpoint) is recoverable, not a denial. Show the error screen with Retry, never
        // the terminal no-access screen.
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
    // NB: `account` is intentionally NOT a dependency — we key on the STABLE `accountId` so
    // this runs once per identity, not on every render (see the account derivation above).
  }, [accountId, instance, nonce]);

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  const value = useMemo<AccessContextValue>(
    () => ({
      status,
      profile,
      diagnostics: {
        email: account?.username ?? null,
        tokenError,
        profileError,
        profileStatus,
      },
      retry,
    }),
    [status, profile, account, tokenError, profileError, profileStatus, retry],
  );

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

/** Resolved access state for the gate + nav. Throws outside `<AccessProvider>` (RootGate). */
export function useAccess(): AccessContextValue {
  const value = useContext(AccessContext);
  if (value === null) {
    throw new Error('useAccess must be used within <AccessProvider> (mounted by RootGate).');
  }
  return value;
}
